/**
 * The reminder choice in the assignment composer (molnfast-react#1613):
 * pre-filled from the instance default, sent to guestTokenApi.create as
 * `reminders`. An order that already has a schedule, on or turned off,
 * keeps it (the callable never overwrites one), so nothing is sent then. An order without a due date
 * may get one here; it is saved before the delegation.
 */
import { useEffect, useState } from 'react';
import { useReminderDefaults } from './useReminderDefaults';
import type { WorkOrder } from '../../types/workOrders';
import {
    ReminderDraft,
    ReminderTiming,
    draftFromTiming,
    scheduleTiming,
    timingFromDraft,
} from '../../types/workOrderReminders';

export interface ComposerReminders {
    /** The order has a schedule (on or off); then the composer only shows it */
    hasSchedule: boolean;
    /** That schedule's timing; null when it is turned off */
    existing: ReminderTiming | null;
    hasDueDate: boolean;
    loading: boolean;
    draft: ReminderDraft | null;
    setDraft: (draft: ReminderDraft) => void;
    /** YYYY-MM-DD typed for an order without one ('' = none) */
    dueDate: string;
    setDueDate: (date: string) => void;
    /** Why the choice cannot be sent */
    error?: string;
    /** For the payload: undefined (keep the existing one), null (none) or the timing */
    reminders: ReminderTiming | null | undefined;
    /** For workOrdersApi.updateDueDate before delegating; null = leave it */
    newDueDate: string | null;
}

export const useComposerReminders = (
    active: boolean,
    workOrder: Pick<WorkOrder, 'dueDate' | 'reminders'>
): ComposerReminders => {
    const schedule = workOrder.reminders?.schedule;
    const hasSchedule = Boolean(schedule);
    const existing = scheduleTiming(schedule);
    const hasDueDate = Boolean(workOrder.dueDate);
    const { defaults, loading, error: defaultsError } = useReminderDefaults(active && !hasSchedule);
    const [draft, setDraft] = useState<ReminderDraft | null>(null);
    const [dueDate, setDueDate] = useState('');

    useEffect(() => {
        if (!active) {
            setDraft(null);
            setDueDate('');
        } else if (!loading && draft === null) {
            setDraft(draftFromTiming(defaults));
        }
    }, [active, loading, defaults, draft]);

    const result = draft ? timingFromDraft(draft) : { timing: null };
    return {
        hasSchedule,
        existing,
        hasDueDate,
        loading: !hasSchedule && (loading || draft === null),
        draft,
        setDraft,
        dueDate,
        setDueDate,
        // A failed read of the default must not quietly send the built-in default.
        error: hasSchedule ? undefined : defaultsError ?? result.error,
        reminders: hasSchedule ? undefined : result.timing ?? null,
        // Only while reminders are on: the date field is shown only then.
        newDueDate: !hasSchedule && !hasDueDate && draft?.enabled && dueDate ? dueDate : null,
    };
};
